<template>
  <analytics-sub-page
    title="Analytics"
    icon="chart-pie"
    css-class="analytics-homepage"
    default-label="Summary"
    :get-sheet-config="getSheetConfig"
    :section-specs="sectionSpecs"
    :download-pending="downloadAllBusy"
  >
    <template #download-menu-items>
      <b-dropdown-item-button :disabled="downloadAllBusy" @click="downloadFullReport">
        Download full report
      </b-dropdown-item-button>
    </template>
    <template #header-actions>
      <download-all-button
        ref="downloadAllRef"
        :homepage-sections="homepageSections"
        :page-extras="getSheetConfig"
        :homepage-busy="homepageBusy"
      />
    </template>
    <snapshot-metrics ref="snapshotRef" />
    <adoption-metrics ref="adoptionRef" />
    <app-logins-metrics ref="appLoginsRef" />
    <app-features-metrics ref="appFeaturesRef" />
  </analytics-sub-page>
</template>

<script setup>
import { computed, ref } from 'vue';
import AnalyticsSubPage from '@/components/AnalyticsSubPage.vue';
import AdoptionMetrics from '@/pages/analyticsHomepage/components/AdoptionMetrics.vue';
import AppFeaturesMetrics from '@/pages/analyticsHomepage/components/AppFeaturesMetrics.vue';
import AppLoginsMetrics from '@/pages/analyticsHomepage/components/AppLoginsMetrics.vue';
import DownloadAllButton from '@/pages/analyticsHomepage/components/DownloadAllButton.vue';
import SnapshotMetrics from '@/pages/analyticsHomepage/components/SnapshotMetrics.vue';
import { wholeSection } from '@/utils/analyticsSections';

const snapshotRef = ref(null);
const adoptionRef = ref(null);
const appLoginsRef = ref(null);
const appFeaturesRef = ref(null);
const downloadAllRef = ref(null);

const downloadAllBusy = computed(() => !!downloadAllRef.value?.busy);

function downloadFullReport() {
  downloadAllRef.value?.download();
}

const getSheetConfig = () => snapshotRef.value?.getPdfData?.() || {};

const sectionSpecs = [
  { key: 'adoption', label: 'Adoption', ref: adoptionRef },
  { key: 'app-logins', label: 'App engagement', ref: appLoginsRef },
  { key: 'app-features', label: 'App features', ref: appFeaturesRef },
];

// "Download full report" reuses these already-loaded, already-rendered
// homepage sections instead of re-mounting copies inside the capture host.
// Captured from the live page DOM, so their charts render at the page's actual
// width rather than the host's fixed 1200px — an accepted trade-off for
// dropping four redundant re-fetches per download.
const homepageSections = () => [
  wholeSection(adoptionRef, 'Adoption'),
  wholeSection(appLoginsRef, 'App engagement'),
  wholeSection(appFeaturesRef, 'App features'),
];

// True while any homepage-native section is (re)fetching — e.g. after a
// division/time-series switch. The capture host polls this in ensureReady so a
// "Download full report" fired mid-refetch waits for fresh data instead of
// silently dropping a not-yet-loaded section (hasData() === false) or capturing
// blank Summary KPIs.
const homepageBusy = () =>
  [snapshotRef, adoptionRef, appLoginsRef, appFeaturesRef].some(sectionRef => !!sectionRef.value?.loading);
</script>
